import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb';
import { SidebarTrigger } from './ui/sidebar';
import {
  USAGE_ANALYTICS_TABS,
  UsageAnalyticsTabs,
  usageAnalyticsTabLabel,
  type UsageAnalyticsTab,
} from './ViewModeToggle';
import { FeatureFlag, useFeatureFlag } from '../services/featureFlag.service';

export const USAGE_ANALYTICS_BASE_PATH = '/usage-analytics';
/** Tab you land on from the sidebar / a bare /usage-analytics visit. */
export const USAGE_ANALYTICS_DEFAULT_PATH = `${USAGE_ANALYTICS_BASE_PATH}/embed-sessions`;
/** Where the retired LLM Metering paths forward to — always this tab, whatever the default is. */
export const USAGE_ANALYTICS_ELLIE_USAGE_PATH = `${USAGE_ANALYTICS_BASE_PATH}/ellie-usage`;

interface UsageAnalyticsPageHeaderProps {
  tab: UsageAnalyticsTab;
  /** Page-specific controls rendered on the right of the header. */
  children?: ReactNode;
}

/**
 * Shared header for the admin Usage Analytics tabs (Embed Sessions / Ellie Usage).
 * Each tab is its own route, so the switcher lives here and navigates.
 */
export function UsageAnalyticsPageHeader({ tab, children }: UsageAnalyticsPageHeaderProps) {
  const navigate = useNavigate();
  // Ellie Usage is the old LLM Metering dashboard, which has always been behind
  // the cost-breakdowns flag — don't advertise the tab to tenants without it.
  const costBreakdownsEnabled = useFeatureFlag(FeatureFlag.COST_BREAKDOWNS, false);
  const tabs = USAGE_ANALYTICS_TABS.filter((t) => t.value !== 'ellie-usage' || costBreakdownsEnabled || tab === 'ellie-usage');

  return (
    <div className="flex flex-col lg:flex-row lg:items-center justify-between w-full px-4 md:px-6 py-3 md:py-4 border-b border-border bg-background flex-shrink-0 z-10 gap-3">
      <div className="flex items-start gap-2 min-w-0">
        <SidebarTrigger className="md:hidden h-5 w-5" />
        <div className="flex flex-col gap-1 md:gap-2 min-w-0">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={USAGE_ANALYTICS_BASE_PATH}>Usage Analytics</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{usageAnalyticsTabLabel(tab)}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <h1 className="text-lg md:text-xl font-semibold text-foreground">Usage Analytics</h1>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <UsageAnalyticsTabs
          value={tab}
          options={tabs}
          onChange={(next) => navigate(`${USAGE_ANALYTICS_BASE_PATH}/${next}`)}
        />
        {children}
      </div>
    </div>
  );
}
